"use client";

import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

type Props = {
  requirements?: string[];
  description?: string;
  outcomes?: string[];
};

const DEFAULT_REQUIREMENTS = ["None."];
const DEFAULT_DESCRIPTION =
  "Content details are coming soon. Check back later for updated requirements and outcomes.";
const DEFAULT_OUTCOMES: string[] = [];

export function CourseRequirementsDescription({ requirements, description, outcomes }: Props) {
  const [expanded, setExpanded] = useState(false);

  const req = useMemo(() => requirements?.length ? requirements : DEFAULT_REQUIREMENTS, [requirements]);
  const desc = description?.trim() ? description : DEFAULT_DESCRIPTION;
  const outs = outcomes?.length ? outcomes : DEFAULT_OUTCOMES;

  return (
    <div className="mt-8 space-y-10">
      <section>
        <h2 className="text-lg font-black text-slate-900 md:text-xl">Requirements</h2>
        <ul className="mt-4 list-disc pl-5 text-sm leading-relaxed text-slate-600 marker:text-slate-400">
          {req.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-black text-slate-900 md:text-xl">Description</h2>
        <p className="mt-4 text-sm leading-relaxed text-slate-600">
          {desc}
        </p>
        {outs.length ? (
          <p className="mt-5 text-sm font-semibold text-slate-800">By the end of this course, you will:</p>
        ) : null}

        {outs.length ? (
          <>
            <div className="relative mt-3">
              <div className={expanded ? "" : "max-h-36 overflow-hidden md:max-h-40"}>
                <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-600 marker:text-slate-400">
                  {outs.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
              {!expanded ? (
                <div
                  className="pointer-events-none absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-white to-transparent"
                  aria-hidden
                />
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:text-violet-800"
            >
              {expanded ? "Show less" : "Show more"}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                aria-hidden
              />
            </button>
          </>
        ) : null}
      </section>
    </div>
  );
}
